import React from 'react';
import { Avatar, AvatarGroup } from '@withjoy/joykit';
import type { GuestStay } from './GuestStayAvatars.types';

// Deliberately smaller than joykit's `xs` (24px): a map pin is a compact bubble, and anything
// larger pushes neighbouring pins into each other.
export const STAY_AVATAR_SIZE = 20;
export const MAX_STAY_AVATARS = 3;
// AvatarGroup's auto spacing is -40% of the avatar size. At 20px that clips the second initial of
// the avatar underneath ("DO" reads as "DC"), which defeats the point of showing faces at all.
// Two initials need almost the full circle, so overlap only enough to read as a stack.
export const STAY_AVATAR_SPACING = -2;

type Props = Readonly<{
  guests: ReadonlyArray<GuestStay>;
  // Falls back to this when a stay carries no first name (plus-ones, imported bookings).
  fallbackFirstName: string;
  size?: number;
  max?: number;
}>;

/**
 * Stacked initials for the guests staying at one place. Shared between the accommodations map
 * marker and the Guest Stays map panel so both read as the same object.
 *
 * Pass every guest — AvatarGroup derives its "+N" overflow from total child count, so pre-slicing
 * would undercount the overflow badge.
 */
export const GuestStayAvatars: React.FC<Props> = ({ guests, fallbackFirstName, size = STAY_AVATAR_SIZE, max = MAX_STAY_AVATARS }) => {
  if (guests.length === 0) return null;

  return (
    <AvatarGroup
      size={size}
      max={max}
      spacing={STAY_AVATAR_SPACING}
    >
      {guests.map(guest => (
        <Avatar
          key={guest.key}
          name={{ firstName: guest.firstName || fallbackFirstName, lastName: guest.lastName }}
        />
      ))}
    </AvatarGroup>
  );
};
